import React from "react";
import {
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Chip,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Notifications,
  ShoppingCart,
  CheckCircle,
  Cancel,
  LocalShipping,
  Assignment,
  Info,
  Delete,
  MarkEmailRead,
} from "@mui/icons-material";
import { Notification } from "../types";
import { formatDistanceToNow } from "date-fns";
import { tr } from "date-fns/locale";

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  onOrderClick?: (orderId: string) => void;
}

const getNotificationIcon = (type: Notification["type"]) => {
  switch (type) {
    case "order_created":
      return <ShoppingCart color="primary" />;
    case "order_approved":
      return <CheckCircle color="success" />;
    case "order_rejected":
      return <Cancel color="error" />;
    case "order_assigned":
      return <Assignment color="info" />;
    case "order_in_transit":
      return <LocalShipping color="warning" />;
    case "order_delivered":
      return <CheckCircle color="success" />;
    case "order_cancelled":
      return <Cancel color="error" />;
    case "system":
      return <Info color="info" />;
    default:
      return <Notifications color="primary" />;
  }
};

const getNotificationColor = (type: Notification["type"]) => {
  switch (type) {
    case "order_created":
      return "primary";
    case "order_approved":
      return "success";
    case "order_rejected":
      return "error";
    case "order_assigned":
      return "info";
    case "order_in_transit":
      return "warning";
    case "order_delivered":
      return "success";
    case "order_cancelled":
      return "error";
    case "system":
      return "info";
    default:
      return "default";
  }
};

const getNotificationTypeLabel = (type: Notification["type"]) => {
  switch (type) {
    case "order_created":
      return "Yeni Sipariş";
    case "order_approved":
      return "Sipariş Onaylandı";
    case "order_rejected":
      return "Sipariş Reddedildi";
    case "order_assigned":
      return "Sipariş Atandı";
    case "order_in_transit":
      return "Sipariş Yolda";
    case "order_delivered":
      return "Sipariş Teslim Edildi";
    case "order_cancelled":
      return "Sipariş İptal Edildi";
    case "system":
      return "Sistem";
    default:
      return "Genel";
  }
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDelete,
  onOrderClick,
}) => {
  const handleMarkAsRead = () => {
    console.log("Mark as read clicked:", notification._id || notification.id);
    if (!notification.is_read) {
      onMarkAsRead(notification._id || notification.id);
    }
  };

  const handleDelete = () => {
    console.log("Delete clicked:", notification._id || notification.id);
    onDelete(notification._id || notification.id);
  };

  const handleOrderClick = () => {
    console.log("Order click:", notification.related_order);
    if (notification.related_order && onOrderClick) {
      onOrderClick(
        notification.related_order._id || notification.related_order.id
      );
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: tr,
      });
    } catch {
      return "Az önce";
    }
  };

  return (
    <ListItem
      sx={{
        backgroundColor: notification.is_read ? "transparent" : "action.hover",
        borderLeft: `4px solid ${
          notification.is_read ? "transparent" : "primary.main"
        }`,
        "&:hover": {
          backgroundColor: "action.hover",
        },
      }}
    >
      <ListItemIcon>{getNotificationIcon(notification.type)}</ListItemIcon>

      <ListItemText
        primary={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: notification.is_read ? "normal" : "bold",
                color: notification.is_read ? "text.secondary" : "text.primary",
              }}
            >
              {notification.title}
            </Typography>
            <Chip
              label={getNotificationTypeLabel(notification.type)}
              size="small"
              color={getNotificationColor(notification.type) as any}
              variant="outlined"
            />
          </Box>
        }
        secondary={
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: notification.is_read ? "text.secondary" : "text.primary",
                mb: 1,
              }}
            >
              {notification.message}
            </Typography>

            {notification.related_order && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography variant="caption" color="text.secondary">
                  Sipariş: {notification.related_order.order_number}
                </Typography>
                <Chip
                  label={notification.related_order.customer_name}
                  size="small"
                  variant="outlined"
                  onClick={handleOrderClick}
                  sx={{ cursor: "pointer" }}
                />
              </Box>
            )}

            <Typography variant="caption" color="text.secondary">
              {formatDate(notification.created_at || notification.createdAt)}
            </Typography>
          </Box>
        }
      />

      <Box sx={{ display: "flex", gap: 0.5 }}>
        {!notification.is_read && (
          <Tooltip title="Okundu olarak işaretle">
            <IconButton size="small" onClick={handleMarkAsRead}>
              <MarkEmailRead fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Bildirimi sil">
          <IconButton size="small" onClick={handleDelete} color="error">
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </ListItem>
  );
};
